// ===================================
// INTERVIEW PAGE - STAGE 8 UPDATE + CONVERSATION PERSISTENCE
// src/pages/Interview.js
// Enhanced with VoiceInterview component integration
// FIX: Properly handles resuming existing conversations
// ===================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSession } from '../services/sessionsApi';
import VoiceInterview from '../components/VoiceInterview';
import MessageContent from '../components/MessageContent';
import RecommendationCard, { isRecommendationMessage } from '../components/RecommendationCard';

import { 
  startInterview, 
  sendMessage, 
  endInterview, 
  getHint,
  getInterviewContext 
} from '../services/interviewApi';
import './Interview.css';

const Interview = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  
  // Session state
  const [session, setSession] = useState(null);
  const [sessionContext, setSessionContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Interview state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [interviewEnded, setInterviewEnded] = useState(false);
  const [waitingForAI, setWaitingForAI] = useState(false);
  
  // Timer state
  const [remainingTime, setRemainingTime] = useState(0);
  const [timerWarning, setTimerWarning] = useState(false);
  const timerRef = useRef(null);
  
  // Voice state (for browser-based fallback)
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [transcript, setTranscript] = useState('');
  
  // Realtime feedback state
  const [showHint, setShowHint] = useState(false);
  const [currentHint, setCurrentHint] = useState('');
  const [loadingHint, setLoadingHint] = useState(false);
  const [lastQuestion, setLastQuestion] = useState('');
  const [processingScore, setProcessingScore] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  
  // Check voice support on mount
  useEffect(() => {
    const speechRecognitionSupported = 'SpeechRecognition' in window || 'webkitSpeechRecognition' in window;
    const speechSynthesisSupported = 'speechSynthesis' in window;
    setVoiceSupported(speechRecognitionSupported && speechSynthesisSupported);
    
    if (speechSynthesisSupported) {
      synthRef.current = window.speechSynthesis;
      window.speechSynthesis.getVoices();
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  // Load session data and context
  useEffect(() => {
    const loadSession = async () => {
      try {
        setLoading(true);
        
        // Load basic session data
        const data = await getSession(sessionId);
        console.log('📦 Loaded session:', data);
        setSession(data);
        
        // Load full context (resume, JD, etc.)
        try {
          const context = await getInterviewContext(sessionId);
          setSessionContext(context);
          console.log('📋 Loaded context:', context);
          
          // FIX: Load existing transcript from context for resume functionality
          if (context.transcript && Array.isArray(context.transcript) && context.transcript.length > 0) {
            console.log(`♻️ Loading existing transcript with ${context.transcript.length} messages`);
            setMessages(context.transcript);
            setInterviewStarted(true);
            
            // Find last AI question for hint feature
            const lastAiMessage = [...context.transcript].reverse().find(m => m.role === 'assistant');
            if (lastAiMessage) {
              setLastQuestion(lastAiMessage.content);
            }
          }
        } catch (contextErr) {
          console.log('Could not load context:', contextErr.message);
        }
        
        // Set voice mode for text + browser speech (NOT for VAPI voice)
        // VAPI voice sessions use the VoiceInterview component instead
        if (data.interview_type === 'web_voice') {
          setIsVoiceMode(true);
        }
        
        // Calculate remaining time
        if (data.started_at && data.status === 'active') {
          const elapsed = (Date.now() - new Date(data.started_at).getTime()) / 1000;
          const remaining = Math.max(0, (data.duration * 60) - elapsed);
          setRemainingTime(Math.floor(remaining));
          setInterviewStarted(true);
          
          // Load transcript from session data as fallback (if not loaded from context)
          if (data.transcript && Array.isArray(data.transcript) && data.transcript.length > 0) {
            if (messages.length === 0) {
              setMessages(data.transcript);
            }
          }
        } else {
          setRemainingTime(data.duration * 60);
        }
        
        if (data.status === 'completed') {
          setInterviewEnded(true);
        }
      } catch (err) {
        console.error('Error loading session:', err);
        setError('Failed to load session');
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) {
      loadSession();
    }
  }, [sessionId]);

  // Timer countdown
  useEffect(() => {
    if (interviewStarted && !interviewEnded && remainingTime > 0) {
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          const newTime = prev - 1;
          if (newTime <= 60 && !timerWarning) {
            setTimerWarning(true);
          }
          if (newTime <= 0) {
            handleEndInterview();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [interviewStarted, interviewEnded, timerWarning]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start interview (for text mode)
  // FIX: Now handles resuming existing conversations
  const handleStartInterview = async () => {
    try {
      setLoading(true);
      setWaitingForAI(true);
      
      const response = await startInterview(sessionId);
      
      setSession(prev => ({
        ...prev,
        ...response.session,
        status: 'active'
      }));
      
      // FIX: Check if this is resuming an existing conversation
      if (response.isResuming && response.existingTranscript && response.existingTranscript.length > 0) {
        // Resuming - use existing transcript
        console.log(`♻️ Resuming interview with ${response.existingTranscript.length} existing messages`);
        setMessages(response.existingTranscript);
        
        // Find last AI question for hint feature
        const lastAiMessage = [...response.existingTranscript].reverse().find(m => m.role === 'assistant');
        if (lastAiMessage) {
          setLastQuestion(lastAiMessage.content);
        }
      } else {
        // New interview - add initial AI message
        const aiMessage = {
          role: 'assistant',
          content: response.initialMessage,
          timestamp: new Date().toISOString()
        };
        setMessages([aiMessage]);
        setLastQuestion(response.initialMessage);
        
        // Speak if voice mode (browser speech)
        if (isVoiceMode && voiceSupported) {
          speakMessage(response.initialMessage);
        }
      }
      
      setInterviewStarted(true);
      setWaitingForAI(false);
    } catch (err) {
      console.error('Start interview error:', err);
      setError(err.response?.data?.message || 'Failed to start interview');
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    const messageToSend = inputMessage.trim() || transcript.trim();
    if (!messageToSend || waitingForAI) return;
    
    // Add user message
    const userMessage = {
      role: 'user',
      content: messageToSend,
      timestamp: new Date().toISOString()
    };
    
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputMessage('');
    setTranscript('');
    setWaitingForAI(true);
    setShowHint(false);
    setCurrentHint('');
    
    try {
      const response = await sendMessage(sessionId, messageToSend, updatedMessages);
      
      // Add AI response
      const aiMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: response.timestamp
      };
      setMessages(prev => [...prev, aiMessage]);
      setLastQuestion(response.response);
      
      // Speak if voice mode
      if (isVoiceMode && voiceSupported) {
        speakMessage(response.response);
      }
    } catch (err) {
      console.error('Send message error:', err);
      setError(err.response?.data?.message || 'Failed to send message');
    } finally {
      setWaitingForAI(false);
    }
  };

  // Get hint for realtime feedback
  const handleGetHint = async () => {
    if (!session?.realtime_feedback || loadingHint) return;
    
    const userMessage = inputMessage.trim() || transcript.trim();
    if (!userMessage) {
      setCurrentHint('Start typing your answer to get feedback!');
      setShowHint(true);
      return;
    }
    
    try {
      setLoadingHint(true);
      const response = await getHint(sessionId, userMessage, lastQuestion);
      setCurrentHint(response.hint);
      setShowHint(true);
    } catch (err) {
      console.error('Get hint error:', err);
      setCurrentHint('Unable to get feedback right now. Keep going!');
      setShowHint(true);
    } finally {
      setLoadingHint(false);
    }
  };

  // End interview
// End interview - with processing indicator

const handleEndInterview = async () => {
  if (timerRef.current) {
    clearInterval(timerRef.current);
  }
  
  try {
    // Show processing overlay
    setProcessingScore(true);
    
    const response = await endInterview(sessionId, messages);
    
    setInterviewEnded(true);
    setSession(prev => ({
      ...prev,
      status: 'completed',
      evaluation: response.evaluation
    }));
    
    // Navigate to session detail
    navigate(`/app/sessions/${sessionId}`);
  } catch (err) {
    console.error('End interview error:', err);
    setError(err.response?.data?.message || 'Failed to end interview');
    setProcessingScore(false);
  }
  // Note: Don't set processingScore to false on success - we're navigating away
};

  // Handle VoiceInterview completion (VAPI voice sessions)
  const handleVoiceInterviewEnd = (results) => {
    console.log('Voice interview ended:', results);
    // Navigate to session detail to see results
    navigate(`/app/sessions/${sessionId}`);
  };

  // Voice functions (browser speech fallback)
  const speakMessage = (text) => {
    if (!synthRef.current) return;
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    
    synthRef.current.speak(utterance);
  };

  const toggleVoiceInput = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    
    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      setTranscript(prev => prev + finalTranscript);
      setInputMessage(prev => prev + finalTranscript || interimTranscript);
    };
    
    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };
    
    recognitionRef.current.onend = () => {
      setIsListening(false);
    };
    
    recognitionRef.current.start();
    setIsListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  };

  

  // Loading state
  if (loading && !session) {
    
    return (
      <div className="interview-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading interview session...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !session) {
    return (
      <div className="interview-page">
              {processingScore && (
        <div className="processing-overlay">
          <div className="processing-content">
            <div className="processing-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
              <div className="spinner-ring"></div>
            </div>
            <h2 className="processing-title">Analyzing Your Interview</h2>
            <p className="processing-text">Our AI is evaluating your responses...</p>
            <div className="processing-steps">
              <div className="processing-step active">
                <span className="step-icon">✓</span>
                <span className="step-text">Interview completed</span>
              </div>
              <div className="processing-step active">
                <span className="step-icon">⏳</span>
                <span className="step-text">Analyzing responses</span>
              </div>
              <div className="processing-step">
                <span className="step-icon">📊</span>
                <span className="step-text">Generating feedback</span>
              </div>
            </div>
          </div>
        </div>
      )}
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h2>Error</h2>
          <p>{error}</p>
          <Link to="/app/sessions" className="btn btn-primary">
            Back to Sessions
          </Link>
        </div>
      </div>
    );
  }

  // =============================================
  // VOICE INTERVIEW (VAPI) - Full screen component
  // =============================================
  if (session?.interview_type === 'voice' && session?.status !== 'completed') {
    return (
      <div className="interview-page">
        <header className="interview-header">
          <div className="header-left">
            <Link to="/app/sessions" className="back-link">← Back</Link>
            <div className="session-badge">
              🎙️ Voice Interview
              {sessionContext?.category?.name && (
                <span className="subcategory-tag"> • {sessionContext.category.name}</span>
              )}
            </div>
          </div>
        </header>
        
        <div className="interview-content voice-content">
          <VoiceInterview 
            session={session} 
            onEnd={handleVoiceInterviewEnd}
          />
        </div>
      </div>
    );
  }

  // =============================================
  // PHONE INTERVIEW - Instructions only
  // =============================================
  if (session?.interview_type === 'phone' && session?.status !== 'completed') {
    return (
      <div className="interview-page">
        <header className="interview-header">
          <div className="header-left">
            <Link to="/app/sessions" className="back-link">← Back</Link>
            <div className="session-badge">
              📞 Phone Interview
            </div>
          </div>
        </header>
        
        <div className="interview-content">
          <div className="pre-interview">
            <div className="pre-interview-card phone-card">
              <div className="pre-interview-icon">📞</div>
              <h1>Phone Interview</h1>
              <p>Your interview will be conducted via phone call.</p>
              
              <div className="phone-instructions">
                <h4>📞 How to Start Your Interview</h4>
                <div className="phone-number">
                  <span className="number-label">Call this number:</span>
                  <span className="number-value">
                    {process.env.REACT_APP_PHONE_NUMBER || '+1 (555) 123-4567'}
                  </span>
                </div>
                <ol className="phone-steps">
                  <li>Call the number above from your phone</li>
                  <li>When prompted, enter your access code using the keypad</li>
                  <li>Your access code: <strong className="access-code">{session?.access_code || 'Loading...'}</strong></li>
                  <li>Your AI interview will begin automatically</li>
                </ol>
                <p className="phone-note">
                  <strong>Note:</strong> Phone interviews are conducted via voice call, not through this website.
                  Your session is {session?.duration} minutes long.
                </p>
              </div>

              <div className="session-details">
                <div className="detail-item">
                  <span className="detail-label">Duration</span>
                  <span className="detail-value">{session?.duration} minutes</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Style</span>
                  <span className="detail-value">{session?.interviewer_personality}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Level</span>
                  <span className="detail-value">{session?.experience_level}</span>
                </div>
              </div>

              <div className="phone-actions">
                <a 
                  href={`tel:${process.env.REACT_APP_PHONE_NUMBER || '+15551234567'}`}
                  className="btn btn-primary btn-lg"
                >
                  📞 Call Now
                </a>
                <Link to="/app/sessions" className="btn btn-secondary">
                  Back to Sessions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =============================================
  // TEXT INTERVIEW (and web_voice browser fallback)
  // =============================================
  return (
    <div className="interview-page">
      {/* Header */}
      <header className="interview-header">
        <div className="header-left">
          <Link to="/app/sessions" className="back-link">← Back</Link>
          <div className="session-badge">
            {sessionContext?.category?.name || session?.interview_type || 'Interview'}
            {sessionContext?.subcategory?.name && (
              <span className="subcategory-tag"> • {sessionContext.subcategory.name}</span>
            )}
          </div>
        </div>
        
        <div className="header-center">
          {interviewStarted && !interviewEnded && (
            <div className={`timer ${timerWarning ? 'warning' : ''}`}>
              <span className="timer-icon">⏱️</span>
              <span className="timer-value">{formatTime(remainingTime)}</span>
            </div>
          )}
        </div>
        
        <div className="header-right">
          {interviewStarted && !interviewEnded && (
            <button 
              onClick={handleEndInterview}
              className="btn btn-danger btn-sm"
              disabled={loading}
            >
              End Interview
            </button>
          )}
        </div>
      </header>

      <div className="interview-content">
        {/* FIX: Resume notice - show if there are existing messages but interview not started */}
        {messages.length > 0 && !interviewStarted && (
          <div className="resume-notice">
            <span>💾</span>
            <span>You have {messages.length} messages from a previous session. Click "Continue Interview" to resume.</span>
          </div>
        )}

        {/* Pre-interview screen */}
        {!interviewStarted && !interviewEnded && (
          <div className="pre-interview">
            <div className="pre-interview-card">
              <div className="pre-interview-icon">🎯</div>
              <h1>Ready to Begin?</h1>
              <p>
                Your <strong>{session?.duration} minute</strong> interview practice is about to start.
              </p>
              
              {/* Session Context Info */}
              <div className="context-info">
                <div className="session-info-grid">
                  <div className="info-item">
                    <span className="info-label">Duration</span>
                    <span className="info-value">{session?.duration} Min</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Style</span>
                    <span className="info-value">{session?.interviewer_personality}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Level</span>
                    <span className="info-value">{session?.experience_level}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Mode</span>
                    <span className="info-value">{isVoiceMode ? 'Voice' : 'Text'}</span>
                  </div>
                </div>

                {/* Category & Subcategory */}
                {sessionContext?.category && (
                  <div className="context-row">
                    <span className="context-icon">📁</span>
                    <div className="context-detail">
                      <span className="context-label">Interview Type</span>
                      <span className="context-value">
                        {sessionContext.category.name}
                        {sessionContext.subcategory && ` → ${sessionContext.subcategory.name}`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Company Name */}
                {session?.company_name && (
                  <div className="context-row">
                    <span className="context-icon">🏢</span>
                    <div className="context-detail">
                      <span className="context-label">Target Company</span>
                      <span className="context-value">{session.company_name}</span>
                    </div>
                  </div>
                )}

                {/* Resume Info */}
                {sessionContext?.resume && (
                  <div className="context-row">
                    <span className="context-icon">📄</span>
                    <div className="context-detail">
                      <span className="context-label">Your Resume</span>
                      <span className="context-value">{sessionContext.resume.name}</span>
                      {sessionContext.resume.data?.career_level && (
                        <span className="context-extra">
                          Level: {sessionContext.resume.data.career_level}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Job Description Info */}
                {sessionContext?.jobDescription && (
                  <div className="context-row">
                    <span className="context-icon">📋</span>
                    <div className="context-detail">
                      <span className="context-label">Job Description</span>
                      <span className="context-value">
                        {sessionContext.jobDescription.extracted?.title || 
                         sessionContext.jobDescription.fileName || 
                         'Provided'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Realtime Feedback Badge */}
                {session?.realtime_feedback && (
                  <div className="context-row highlight">
                    <span className="context-icon">💡</span>
                    <div className="context-detail">
                      <span className="context-label">Realtime Feedback</span>
                      <span className="context-value">Enabled - Get hints while you answer!</span>
                    </div>
                  </div>
                )}
              </div>
              
              {isVoiceMode && voiceSupported && (
                <div className="voice-notice">
                  <span className="voice-icon">🎙️</span>
                  <div>
                    <strong>Voice Mode Enabled</strong>
                    <p>The AI will speak to you and listen to your responses.</p>
                  </div>
                </div>
              )}
              
              <div className="tips-box">
                <h4>💡 Quick Tips</h4>
                <ul>
                  <li>Find a quiet place with no distractions</li>
                  <li>Use the STAR method for behavioral questions</li>
                  <li>Take a moment to think before responding</li>
                  {session?.realtime_feedback && (
                    <li>Click the hint button for realtime feedback</li>
                  )}
                </ul>
              </div>
              
              {/* FIX: Show different button text when resuming */}
              <button 
                onClick={handleStartInterview}
                className="btn btn-primary btn-lg"
                disabled={loading}
              >
                {loading ? 'Starting...' : messages.length > 0 ? '♻️ Continue Interview' : 'Start Interview'}
              </button>
            </div>
          </div>
        )}

        {/* Interview in progress */}
        {interviewStarted && !interviewEnded && (
          <>
            {/* Messages */}
            <div className="messages-area">
              {messages.length === 0 && waitingForAI && (
                <div className="ai-thinking">
                  <div className="thinking-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <p>Interviewer is preparing...</p>
                </div>
              )}
              
              {messages.map((msg, index) => (
                <div key={index} className={`message ${msg.role}`}>
                  <div className="message-avatar">
                    {msg.role === 'user' ? '👤' : '🎯'}
                  </div>
                  <div className="message-content">
<div className="message-text">
  {msg.role === 'assistant' && isRecommendationMessage(msg.content) ? (
    <RecommendationCard content={msg.content} />
  ) : (
    <MessageContent content={msg.content} role={msg.role} />
  )}
</div>                    <div className="message-time">
                      {new Date(msg.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                </div>
              ))}
              
              {waitingForAI && messages.length > 0 && (
                <div className="message assistant">
                  <div className="message-avatar">🎯</div>
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Hint Display */}
            {showHint && currentHint && (
              <div className="hint-banner">
                <span className="hint-icon">💡</span>
                <span className="hint-text">{currentHint}</span>
                <button 
                  className="hint-close"
                  onClick={() => setShowHint(false)}
                >
                  ×
                </button>
              </div>
            )}

            {/* Input area */}
            <div className="input-area">
              <form onSubmit={handleSendMessage} className="message-form">
                {isVoiceMode && voiceSupported && (
                  <button
                    type="button"
                    onClick={toggleVoiceInput}
                    className={`voice-btn ${isListening ? 'listening' : ''}`}
                    disabled={isSpeaking || waitingForAI}
                  >
                    {isListening ? '🔴' : '🎤'}
                  </button>
                )}
                
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={isListening ? "Listening..." : "Type your response..."}
                  disabled={waitingForAI || isSpeaking}
                  className="message-input"
                />
                
                {/* Hint Button - only if realtime feedback enabled */}
                {session?.realtime_feedback && (
                  <button
                    type="button"
                    onClick={handleGetHint}
                    className="hint-btn"
                    disabled={loadingHint || waitingForAI}
                    title="Get feedback hint"
                  >
                    {loadingHint ? '...' : '💡'}
                  </button>
                )}
                
                <button
                  type="submit"
                  disabled={(!inputMessage.trim() && !transcript.trim()) || waitingForAI || isSpeaking}
                  className="send-btn"
                >
                  Send
                </button>
              </form>
            </div>
          </>
        )}

        {/* Interview ended */}
        {interviewEnded && (
          <div className="interview-ended">
            <div className="ended-card">
              <div className="ended-icon">✅</div>
              <h1>Interview Complete!</h1>
              <p>Great job completing your practice interview.</p>
              <div className="ended-actions">
                <Link to={`/app/sessions/${sessionId}`} className="btn btn-primary">
                  View Results
                </Link>
                <Link to="/app/book" className="btn btn-secondary">
                  Book Another Session
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Interview;